import React, { useState, useEffect, useMemo, useRef } from 'react';
import { profileAvatar } from "../component/ImageList";
import baseURL from "../component/baseURL";
import { useNavigate, useLocation } from 'react-router-dom';
import showNotification from '../component/Notification';
import { Link } from 'react-router-dom';
import stringsoflanguages from "../component/stringsoflanguages";

function hasDisplayValue(value) {
    if (value == null) return false;
    const normalized = String(value).trim().toLowerCase();
    return normalized !== '' && normalized !== 'null' && normalized !== 'undefined';
}

function getSocialHref(type, value) {
    const raw = String(value ?? '').trim();
    if (!raw) return '#';

    if (type === 'whatsapp') {
        const digits = raw.replace(/[^\d]/g, '');
        return digits ? `https://wa.me/${digits}` : '#';
    }

    if (type === 'skype') {
        return `skype:${raw}?chat`;
    }

    if (type === 'facebook') {
        if (/^https?:\/\//i.test(raw)) return raw;
        return `https://facebook.com/${raw}`;
    }

    if (type === 'instagram') {
        return `https://instagram.com/${raw}`;
    }

    if (type === 'telegram') {
        return `https://t.me/${raw}`;
    }

    if (type === 'linkedin') {
        return `https://linkedin.com/in/${raw}`;
    }

    return '#';
}

/** Single `\n` → one `<br />`, `\n\n` → two `<br />` (blank line between splits). */
function renderContractWithLineBreaks(text) {
    const lines = String(text).split('\n');
    return lines.map((line, i) => (
        <React.Fragment key={i}>
            {i > 0 && <br />}
            {/^https?:\/\//i.test(line.trim()) ? (
                <a
                    href={line.trim()}
                    target="_blank"
                    rel="noreferrer"
                    className="text-white"
                    style={{ textDecoration: 'underline' }}
                >
                    {line}
                </a>
            ) : (
                line
            )}
        </React.Fragment>
    ));
}

export default function MembersProfile({ memberID: memberIDProp, onBack }) {
    const location = useLocation();
    const navigate = useNavigate();
    const memberID = memberIDProp ?? location.state?.memberID;

    const [userID, setUserID] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [gearMenuOpen, setGearMenuOpen] = useState(false);
    const [gearSpinning, setGearSpinning] = useState(false);
    const gearMenuRef = useRef(null);
    const [membersProfile, setMembersProfile] = useState(null);
    const [isPremium, setIsPremium] = useState(false);
    const [FName, setFName] = useState('');
    const [LName, setLName] = useState('');
    const [Title, setTitle] = useState('');
    const [Email, setEmail] = useState('');
    const [Phone, setPhone] = useState('');
    const [LicenceNumber, setLicenceNumber] = useState('');
    const [spokenLanguage, setSpokenLanguage] = useState('');
    const [shortIntroduction, setShortIntroduction] = useState('');
    const [aboutMe, setAboutMe] = useState('');
    const [loadData, setLoadData] = useState(false);
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [listingCount, setListingCount] = useState(0);
    const [connectionCount, setConnectionCount] = useState(0);
    const [portfolioCount, setPortfolioCount] = useState(0);
    const [profileImage, setProfileImage] = useState('');
    const [loading, setLoading] = useState(true);
    const [checkConn, setCheckConn] = useState(0);
    const [connectionConfirmOpen, setConnectionConfirmOpen] = useState(false);
    const [connectionActionType, setConnectionActionType] = useState('add');
    const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
    const [isBlocked, setIsBlocked] = useState(false);
    const [blockActionType, setBlockActionType] = useState('block');
    const [feedOptionsModalOpen, setFeedOptionsModalOpen] = useState(false);
    const [feedReceivedCount, setFeedReceivedCount] = useState(2);
    const [feedSharedCount, setFeedSharedCount] = useState(2);
    const [feedReceivedId, setFeedReceivedId] = useState(null);
    /** From `member_details` response — used as `request_id` for View My Shared */
    const [feedSharedId, setFeedSharedId] = useState(null);
    const [noFeedReceivedModalOpen, setNoFeedReceivedModalOpen] = useState(false);
    const [noFeedRequestedModalOpen, setNoFeedRequestedModalOpen] = useState(false);
    const [contractModalOpen, setContractModalOpen] = useState(false);

    /** `profile.contract` from member_details — null / empty / "null" → show "Not Shared" */
    const contractDisplayValue = useMemo(() => {
        const c = membersProfile?.contract;
        if (c == null) return null;
        const s = String(c).trim();
        if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return null;
        return s;
    }, [membersProfile]);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const handleGearClickOutside = (e) => {
            if (gearMenuRef.current && !gearMenuRef.current.contains(e.target)) {
                setGearMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleGearClickOutside);
        return () => document.removeEventListener('mousedown', handleGearClickOutside);
    }, []);

    useEffect(() => {
        const fromPremiumMembers =
            location.state?.from === '/premium-home' ||
            location.state?.from === '/premium-members' ||
            location.state?.from === '/premium-home/premium-members';
        const premiumFromProfile =
            isPremium === true ||
            membersProfile?.isPremium === true ||
            membersProfile?.is_premium === true ||
            membersProfile?.is_premium === 1;

        const shouldUsePremiumPageClass = fromPremiumMembers || premiumFromProfile;
        if (!shouldUsePremiumPageClass) return;

        document.body.classList.add('premium-page');
        return () => {
            document.body.classList.remove('premium-page');
        };
    }, [location.state, isPremium, membersProfile]);

    useEffect(() => {
        const userid = localStorage.getItem('user_id');
        setUserID(userid);
        if (memberID) {
            setLoading(true);
            fetchMembersProfileAPI(userid, memberID);
        } else {
            setLoading(false);
        }
    }, [memberID]);

    function fetchMembersProfileAPI(userid, memberID) {
        // const navigate = useNavigate();
        let formdata = new FormData();

        formdata.append("user_id", userid)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
        formdata.append("member_id", memberID)

        console.log(formdata)

        fetch(baseURL.URL + '/member/member_details', {
            method: 'POST',

            body: formdata

        }).then((response) => response.json())
            .then((responseJson) => {
                console.log('MEMBER DETAILS ******************')
                //console.log(responseJson);

                if (responseJson.server.result_code === 200) {
                    const result1 = responseJson.response.profile || {};
                    const result2 = responseJson.response;
                    setMembersProfile(result1);
                    setFName(result1.member_fname ?? '');
                    setLName(result1.member_lname ?? '');
                    setTitle(result1.title ?? '');
                    setEmail(result1.email ?? '');
                    setPhone(result1.phone ?? '');
                    setLicenceNumber(result1.licence_number ?? '');
                    setSpokenLanguage(result1.spoken_language ?? '');
                    setShortIntroduction(result1.short_introduction ?? '');
                    setAboutMe(result1.aboutme ?? '');
                    setCity(result1.city_name ?? '');
                    setCountry(result1.country_name ?? '');
                    setProfileImage(result1.member_image ?? result1.member_image_thumb ?? '');
                    setListingCount(result2.listing_count ?? 0);
                    setConnectionCount(result2.connection_count ?? 0);
                    setPortfolioCount(result2.portfolio_count ?? 0);
                    setLoadData(true);
                    setCheckConn(result2.check_conn ?? 0);
                    setIsBlocked(result2.blockstatus ?? true);

                    setFeedReceivedCount(result2.feed_received ?? 0);
                    setFeedSharedCount(result2.feed_shared ?? 0);
                    setFeedReceivedId(result2.feed_received_id ?? null);
                    setFeedSharedId(result2.feed_shared_id ?? null);

                }
                else {
                    console.log(responseJson.server.message)
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
            });
    }


    function addAsUser() {
        // console.log(this.state.userID)
        // console.log('Member ID :' + this.state.memberID)
        let formdata = new FormData();

        formdata.append("user_id", userID)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
        formdata.append("receiver_id", memberID)



        fetch(baseURL.URL + '/member/connection_request', {
            method: 'POST',

            body: formdata

        }).then((response) => response.json())
            .then((responseJson) => {

                //  console.log(responseJson.server.message);

                if (responseJson.server.result_code === 200) {

                    //console.log(responseJson)
                    // this.getMemberDetails()
                    showNotification('Success', responseJson.server.message, 'success');
                    fetchMembersProfileAPI(userID, memberID);
                }
                else {
                    showNotification('Error', responseJson.server.message, 'danger');
                    fetchMembersProfileAPI(userID, memberID);
                }
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', 'Something went wrong while adding as user', 'danger');
            });
    }

    function removeAsUser() {
        console.log('User ID : ' + userID)
        console.log('Member ID :' + memberID)
        let formdata = new FormData();
        formdata.append("user_id", userID)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
        formdata.append("member_id", memberID)

        fetch(baseURL.URL + '/member/del_connection', {
            method: 'POST',
            body: formdata

        }).then((response) => response.json())
            .then((responseJson) => {

                if (responseJson.server.result_code === 200) {

                    //console.log(responseJson)
                    // this.getMemberDetails()
                    showNotification('Success', responseJson.server.message, 'success');
                    fetchMembersProfileAPI(userID, memberID);
                }
                else {
                    showNotification('Error', responseJson.server.message, 'danger');
                    fetchMembersProfileAPI(userID, memberID);
                }
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', 'Something went wrong while removing as user', 'danger');
            });


    }

    function openConnectionConfirm() {
        setConnectionActionType(Number(checkConn) === 0 ? 'add' : 'remove');
        setConnectionConfirmOpen(true);
        setGearMenuOpen(false);
    }

    function handleConfirmConnectionAction() {
        if (connectionActionType === 'add') {
            addAsUser();
        } else {
            removeAsUser();
        }
        setConnectionConfirmOpen(false);
    }

    function openBlockConfirm(action = 'block') {
        setBlockActionType(action);
        setBlockConfirmOpen(true);
        setGearMenuOpen(false);
    }

    function handleConfirmBlockAction() {
        if (blockActionType === 'unblock') {
            unblockUser();
        } else {
            blockUser();
        }
        setBlockConfirmOpen(false);
    }

    function openFeedOptionsModal() {
        setFeedOptionsModalOpen(true);
        setGearMenuOpen(false);
    }

    function openContractModal() {
        setGearMenuOpen(false);
        setContractModalOpen(true);
    }

    function handleFeedReceivedClick() {
        setGearMenuOpen(false);
        const count = Number(feedReceivedCount);
        if (!Number.isFinite(count) || count <= 0) {
            setNoFeedReceivedModalOpen(true);
            return;
        }
        const reqId = feedReceivedId;
        if (!reqId || !memberID) {
            showNotification('Error', 'Feed information is not available.', 'danger');
            return;
        }
        navigate('/feed/incoming-member-feed', {
            state: {
                sender_id: memberID,
                request_id: String(reqId),
            },
        });
    }

    function handleFeedRequestedClick() {
        setGearMenuOpen(false);
        const count = Number(feedSharedCount);
        if (!Number.isFinite(count) || count <= 0) {
            setNoFeedRequestedModalOpen(true);
            return;
        }
        const reqId = feedSharedId;
        if (!reqId || !memberID) {
            showNotification('Error', 'Feed information is not available.', 'danger');
            return;
        }
        navigate('/feed/view-my-shared', {
            state: {
                receiver_id: memberID,
                request_id: String(reqId),
            },
        });
    }

    function blockUser() {
        console.log(userID)
        console.log('Member ID :' + memberID)
        let formdata = new FormData();

        formdata.append("user_id", userID)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
        formdata.append("todo", 'blockuser')
        formdata.append("blocked_id", memberID)



        fetch(baseURL.URL + '/member/block_unblock_users', {
            method: 'POST',

            body: formdata

        }).then((response) => response.json())
            .then((responseJson) => {

                //  console.log(responseJson.server.message);

                if (responseJson.server.result_code === 200) {


                    showNotification('Success', responseJson.server.message, 'success');
                    fetchMembersProfileAPI(userID, memberID);

                }
                else {
                    showNotification('Error', responseJson.server.message, 'danger');
                    fetchMembersProfileAPI(userID, memberID);
                }
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', 'Something went wrong while blocking user', 'danger');
            });
    }

    function unblockUser() {
        console.log(userID)
        console.log('Member ID :' + memberID)
        let formdata = new FormData();

        formdata.append("user_id", userID)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
        formdata.append("todo", 'unblockuser')
        formdata.append("blocked_id", memberID)



        fetch(baseURL.URL + '/member/block_unblock_users', {
            method: 'POST',

            body: formdata

        }).then((response) => response.json())
            .then((responseJson) => {

                //  console.log(responseJson.server.message);

                if (responseJson.server.result_code === 200) {


                    showNotification('Success', responseJson.server.message, 'success');
                    fetchMembersProfileAPI(userID, memberID);
                }
                else {
                    showNotification('Error', responseJson.server.message, 'danger');
                    fetchMembersProfileAPI(userID, memberID);
                }
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', 'Something went wrong while unblocking user', 'danger');
            });

    }

    function requestFeed() {
        if (!userID || !memberID) {
            showNotification('Error', 'Member information is missing', 'danger');
            return;
        }
        if (Number(listingCount) === 0) {
            showNotification('Error', 'Listing not available for this user', 'danger');
            return;
        }
        setFeedOptionsModalOpen(false);
        const formdata = new FormData();
        formdata.append("user_id", userID);
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
        formdata.append("member_id", memberID);

        fetch(baseURL.URL + '/portfolio_feed/feed_request', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                if (responseJson?.server?.result_code === 200) {
                    showNotification('Success', responseJson.server.message, 'success');
                } else {
                    showNotification('Error', responseJson?.server?.message || 'Feed request failed', 'danger');
                }
                fetchMembersProfileAPI(userID, memberID);
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', 'Something went wrong while requesting feed', 'danger');
            });
    }







    function handleProfileBack() {
        const from = location.state?.from;
        const returnTo = location.state?.returnTo;
        const scrollPosition = location.state?.scrollPosition;
        if (from === '/feed/received') {
            navigate('/feed', {
                state: {
                    activeItem: 'Feeds Received'
                }
            });
        } else if (returnTo?.path) {
            const backState = returnTo.state ?? {};
            const isMainMyConnection =
                returnTo.path === '/home/my-connection' && backState.fromSidebar === true;
            const isMainGlobalMembers =
                returnTo.path === '/home/global-members' && backState.fromGlobalMembersRoot === true;
            navigate(returnTo.path, {
                replace: true,
                state: isMainMyConnection
                    ? { fromSidebar: true, scrollPosition: backState.scrollPosition }
                    : isMainGlobalMembers
                        ? { fromGlobalMembersRoot: true, scrollPosition: backState.scrollPosition }
                        : backState,
            });
        } else if (from === '/home/global-members') {
            navigate(from, {
                replace: true,
                state:
                    typeof scrollPosition === 'number'
                        ? { fromGlobalMembersRoot: true, scrollPosition }
                        : { fromGlobalMembersRoot: true },
            });
        } else if (from) {
            navigate(from, typeof scrollPosition === 'number' ? { state: { scrollPosition } } : {});
        } else if (onBack) {
            onBack();
        } else {
            navigate(-1);
        }
    }

    const profileBackLabel =
        location.state?.from === '/home/my-connection'
            ? 'Back to My Connection'
            : location.state?.from === '/home/connection-request'
                ? 'Back to Connection Request'
                : location.state?.from === '/home/global-members'
                    ? 'Back to Global Members'
                    : 'Back';

    const profileBackAriaLabel =
        location.state?.from === '/home/my-connection'
            ? 'Back to My Connection'
            : location.state?.from === '/home/connection-request'
                ? 'Back to Connection Request'
                : 'Back to Global Members';

    const hasAnySocialChannel =
        hasDisplayValue(membersProfile?.social_whatsapp) ||
        hasDisplayValue(membersProfile?.social_skype) ||
        hasDisplayValue(membersProfile?.social_facebook) ||
        hasDisplayValue(membersProfile?.social_instagram) ||
        hasDisplayValue(membersProfile?.social_telegram) ||
        hasDisplayValue(membersProfile?.social_linkedin);

    if (loading) {
        return (
            <div className="content-right global-members-page page-loader-wrapper">
                <div className="react-js-loader">
                    <div className="react-js-loader-spinner"></div>
                    <p className="react-js-loader-text">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="content-right global-members-page members-profile-page">
                {isMobile && (location.state?.from || onBack) && (
                    <div className="mobile-page-header members-profile-mobile-header">
                        <button type="button" className="mobile-back-btn" onClick={handleProfileBack} aria-label={profileBackAriaLabel}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M15 18L9 12L15 6"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <h2 className="mobile-page-title">{profileBackLabel}</h2>
                    </div>
                )}
                {!isMobile && (location.state?.from || onBack) && (
                    <button
                        type="button"
                        className="member-profile-back-btn"
                        onClick={handleProfileBack}
                        aria-label={profileBackAriaLabel}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {profileBackLabel}
                    </button>
                )}
                {/* Search and Filter Section */}
                {/* <div className="search-filter-section">
                <div className="search-bar">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        className="search-icon"
                    >
                        <path
                            d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                            stroke="#E1730B"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M17.5 17.5L13.875 13.875"
                            stroke="#E1730B"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search by name / company"
                        className="search-input"
                    />
                </div>
                <button className="filter-btn">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        height="25"
                        viewBox="0 0 25 25"
                        fill="none"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M16.4062 4.69678C14.5256 4.69678 12.9441 6.04475 12.5793 7.82178H2.34375C1.91228 7.82178 1.5625 8.17156 1.5625 8.60303C1.5625 9.0345 1.91228 9.38428 2.34375 9.38428H12.5793C12.9441 11.1613 14.5256 12.5093 16.4062 12.5093C18.2869 12.5093 19.8684 11.1613 20.2332 9.38428H22.6562C23.0877 9.38428 23.4375 9.0345 23.4375 8.60303C23.4375 8.17156 23.0877 7.82178 22.6562 7.82178H20.2332C19.8684 6.04475 18.2869 4.69678 16.4062 4.69678ZM16.4062 6.25928C17.7099 6.25928 18.75 7.29938 18.75 8.60303C18.75 9.90668 17.7099 10.9468 16.4062 10.9468C15.1026 10.9468 14.0625 9.90668 14.0625 8.60303C14.0625 7.29938 15.1026 6.25928 16.4062 6.25928Z"
                            fill="white"
                        />
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M8.59375 12.5093C6.71313 12.5093 5.1316 13.8573 4.76685 15.6343H2.34375C1.91228 15.6343 1.5625 15.9841 1.5625 16.4155C1.5625 16.847 1.91228 17.1968 2.34375 17.1968H4.76685C5.1316 18.9738 6.71313 20.3218 8.59375 20.3218C10.4744 20.3218 12.0559 18.9738 12.4207 17.1968H22.6562C23.0877 17.1968 23.4375 16.847 23.4375 16.4155C23.4375 15.9841 23.0877 15.6343 22.6562 15.6343H12.4207C12.0559 13.8573 10.4744 12.5093 8.59375 12.5093ZM8.59375 14.0718C9.8974 14.0718 10.9375 15.1119 10.9375 16.4155C10.9375 17.7192 9.8974 18.7593 8.59375 18.7593C7.2901 18.7593 6.25 17.7192 6.25 16.4155C6.25 15.1119 7.2901 14.0718 8.59375 14.0718Z"
                            fill="white"
                        />
                    </svg>
                </button>
            </div> */}

                <div className='members-profile-container'>
                    <div className="members-profile-header">
                        <div className="members-profile-header-left">
                            <div className="members-profile-image-wrapper">
                                <img
                                    className="members-profile-image"
                                    src={profileImage || profileAvatar}
                                    alt={`${FName} ${LName}`}
                                />
                                {isPremium && (
                                    <span className="members-profile-premium-badge" aria-hidden>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#E1730B">
                                            <path d="M12 2L15 9L22 10L17 15L18 22L12 19L6 22L7 15L2 10L9 9L12 2Z" />
                                        </svg>
                                    </span>
                                )}
                            </div>
                            <div className="members-profile-info">
                                <h3 className="members-profile-name">{FName} {LName}{(membersProfile?.isPremium === true || membersProfile?.is_premium === true || membersProfile?.is_premium === 1 || isPremium) ? (
                                    <span className="premiumtype">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
                                            <path d="M7.2868 9.91064L10.7796 22.0513L0 9.91064H7.2868Z" fill="#7323ff" />
                                            <path d="M7.42024 8.38808H0L6.86669 2.01175C7.00926 1.87014 7.20204 1.79102 7.403 1.79102H11.2271L7.42024 8.38808Z" fill="#7323ff" />
                                            <path d="M9.1778 8.38811L12.9841 1.7915L16.7905 8.38811H9.1778Z" fill="#7323ff" />
                                            <path d="M17.098 9.91064L12.9842 24.2091L8.87036 9.91064H17.098Z" fill="#7323ff" />
                                            <path d="M18.5481 8.38813L14.7412 1.79102H19.0727C19.2843 1.79102 19.4857 1.87881 19.6298 2.03361L26 8.38813H18.5481Z" fill="#7323ff" />
                                            <path d="M25.9683 9.91064L15.1887 22.0513L18.6815 9.91064H25.9683Z" fill="#7323ff" />
                                        </svg>
                                    </span>
                                ) : null}
                                    {isBlocked && <span className="bg-red-600 text-white text-sm font-medium px-2 py-1 rounded-md">Member Blocked</span>}</h3>
                                <p className="member-since mb-0">Member since {membersProfile?.added_date}</p>
                                <p className="members-profile-title">{Title}</p>
                                <p className="members-profile-location">{[city, country].filter(Boolean).join(' - ')}</p>
                                <div className="members-profile-contact-btns">
                                    <a href={`tel:${Phone}`} className="members-profile-contact-btn" aria-label="Call">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E1730B" strokeWidth="2">
                                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                        </svg>
                                    </a>
                                    <a href={`mailto:${Email}`} className="members-profile-contact-btn" aria-label="Email">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E1730B" strokeWidth="2">
                                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                            <polyline points="22,6 12,13 2,6" />
                                        </svg>
                                    </a>
                                    <button type="button" className="members-profile-contact-btn" aria-label="Message">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E1730B" strokeWidth="2">
                                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="members-profile-stats-bar">
                            <div className="members-profile-stat-item cursor-pointer" onClick={() => navigate('/home/my-listings', {
                                state: {
                                    is_member: 1,
                                    member_id: memberID || '',
                                    member_fname: FName,
                                    member_lname: LName,
                                    returnTo: {
                                        path: '/member-profile',
                                        state: {
                                            memberID,
                                            member: location.state?.member,
                                            from: location.state?.from || '/home/my-connection',
                                            scrollPosition: location.state?.scrollPosition,
                                            returnTo: location.state?.returnTo,
                                        },
                                    },
                                },
                            })}>
                                <span className="members-profile-stat-count">{listingCount}</span>
                                <span className="members-profile-stat-label">{stringsoflanguages.listings}</span>
                            </div>
                            <div className="members-profile-stat-item cursor-pointer" onClick={() => navigate('/home/my-connection', {
                                state: {
                                    is_member: 1,
                                    member_id: memberID || '',
                                    member_fname: FName,
                                    member_lname: LName,
                                    returnTo: {
                                        path: '/member-profile',
                                        state: {
                                            memberID,
                                            member: location.state?.member,
                                            from: location.state?.from || '/home/my-connection',
                                            scrollPosition: location.state?.scrollPosition,
                                            returnTo: location.state?.returnTo,
                                        },
                                    },
                                },
                            })}>
                                <span className="members-profile-stat-count">{connectionCount}</span>
                                <span className="members-profile-stat-label">{stringsoflanguages.connections}</span>
                            </div>
                            <div className="members-profile-stat-item cursor-pointer" onClick={() => navigate('/home/my-portfolio', {
                                state: {
                                    is_member: 1,
                                    member_id: memberID || '',
                                    member_fname: FName,
                                    member_lname: LName,
                                    returnTo: {
                                        path: '/member-profile',
                                        state: {
                                            memberID,
                                            member: location.state?.member,
                                            from: location.state?.from || '/home/my-connection',
                                            scrollPosition: location.state?.scrollPosition,
                                            returnTo: location.state?.returnTo,
                                        },
                                    },
                                },
                            })}>
                                <span className="members-profile-stat-count">{portfolioCount}</span>
                                <span className="members-profile-stat-label">{stringsoflanguages.portfolio}</span>
                            </div>
                        </div>
                    </div>

                    {/* Profile Details */}
                    <div className="profile-right">

                        <div className="profile-details">
                            <div className="detail-section">
                                <h3 className="section-title">{stringsoflanguages.companyInformation}</h3>
                                <div className="detail-content">
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.companyImage}</div>
                                        <div className="detail-value">
                                            <Link to={membersProfile?.premium_web} target="_blank" rel="noreferrer">
                                                {(membersProfile?.company_image || membersProfile?.company_image) ? (
                                                    <img
                                                        src={membersProfile?.company_image || membersProfile?.company_image}
                                                        alt={membersProfile?.company_name || 'Company'}
                                                        style={{
                                                            width: 72,
                                                            height: 72,
                                                            borderRadius: 8,
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                ) : (
                                                    'N/A'
                                                )}
                                            </Link>
                                        </div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.companyName}</div>
                                        <div className="detail-value">{membersProfile?.company_name || 'N/A'}</div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.yearCreated}</div>
                                        <div className="detail-value">{membersProfile?.company_created_year || 'N/A'}</div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.street}</div>
                                        <div className="detail-value">{membersProfile?.street || 'N/A'}</div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.number}</div>
                                        <div className="detail-value">{membersProfile?.number || 'N/A'}</div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.zipCode}</div>
                                        <div className="detail-value">{membersProfile?.zip || 'N/A'}</div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.city}</div>
                                        <div className="detail-value">{membersProfile?.city_name || 'N/A'}</div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.state}</div>
                                        <div className="detail-value">{membersProfile?.state_name || 'N/A'}</div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.country}</div>
                                        <div className="detail-value">{membersProfile?.country_name || 'N/A'}</div>
                                    </div>
                                </div>
                            </div>

                            {hasAnySocialChannel && (
                                <div className="detail-section social-media-section">
                                    <h3 className="section-title">{stringsoflanguages.mySocialChannels}</h3>
                                    <div className="detail-content">
                                        {hasDisplayValue(membersProfile?.social_whatsapp) && (
                                            <div className="detail-row" onClick={() => window.open(getSocialHref('whatsapp', membersProfile?.social_whatsapp), '_blank')}>
                                                <label className="detail-label"><svg height="512" viewBox="0 0 176 176" width="512" xmlns="http://www.w3.org/2000/svg" id="fi_3670051"><g id="Layer_2" data-name="Layer 2"><g id="_09.whatsapp" data-name="09.whatsapp"><circle id="background" cx="88" cy="88" fill="#29a71a" r="88"></circle><g id="icon" fill="#fff"><path d="m126.8 49.2a54.57 54.57 0 0 0 -87.42 63.13l-5.79 28.11a2.08 2.08 0 0 0 .33 1.63 2.11 2.11 0 0 0 2.24.87l27.55-6.53a54.56 54.56 0 0 0 63.09-87.21zm-8.59 68.56a42.74 42.74 0 0 1 -49.22 8l-3.84-1.9-16.89 4 .05-.21 3.5-17-1.88-3.71a42.72 42.72 0 0 1 7.86-49.59 42.73 42.73 0 0 1 60.42 0 2.28 2.28 0 0 0 .22.22 42.72 42.72 0 0 1 -.22 60.19z"></path><path d="m116.71 105.29c-2.07 3.26-5.34 7.25-9.45 8.24-7.2 1.74-18.25.06-32-12.76l-.17-.15c-12.09-11.21-15.23-20.54-14.47-27.94.42-4.2 3.92-8 6.87-10.48a3.93 3.93 0 0 1 6.15 1.41l4.45 10a3.91 3.91 0 0 1 -.49 4l-2.25 2.92a3.87 3.87 0 0 0 -.35 4.32c1.26 2.21 4.28 5.46 7.63 8.47 3.76 3.4 7.93 6.51 10.57 7.57a3.82 3.82 0 0 0 4.19-.88l2.61-2.63a4 4 0 0 1 3.9-1l10.57 3a4 4 0 0 1 2.24 5.91z"></path></g></g></g></svg> <span>Whatsapp</span> </label>
                                                <div className="detail-value">
                                                    <a
                                                        href={getSocialHref('whatsapp', membersProfile?.social_whatsapp)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {membersProfile?.social_whatsapp}
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                        {hasDisplayValue(membersProfile?.social_skype) && (
                                            <div className="detail-row" onClick={() => window.open(getSocialHref('skype', membersProfile?.social_skype), '_blank')}>
                                                <label className="detail-label"><svg height="512" viewBox="0 0 152 152" width="512" xmlns="http://www.w3.org/2000/svg" id="fi_3670176"><g id="Layer_2" data-name="Layer 2"><g id="Color"><g id="_13.Skype" data-name="13.Skype"><circle id="Background" cx="76" cy="76" fill="#03a9f4" r="76"></circle><path id="Icon" d="m113.62 84.45c5.79-26-17-49.64-43.75-45.11-14.53-9.01-33.87 1.17-33.87 18.46a21.5 21.5 0 0 0 3 11c-5.38 26.07 17.6 49.36 44.33 44.51 18.93 10.02 39.82-9.91 30.29-28.86zm-25.43 15.68c-7 2.89-18.58 2.91-25.57-.75-10-5.32-11.74-17.29-3.14-17.29 6.48 0 4.43 7.56 11 10.64 3 1.39 9.46 1.53 13.21-1s3.31-6.38 1.31-8.25c-5.34-4.93-20.61-3-27.6-11.6a12.54 12.54 0 0 1 .13-15.13c6.5-8.37 25.57-8.88 33.78-2.56 7.57 5.86 5.53 13.64-.59 13.64-7.35 0-3.48-9.62-15.35-9.62-8.6 0-12 6.12-5.93 9.1 8.24 4.08 29.16 2.69 29.16 18.69 0 6.63-4.08 11.58-10.41 14.16z" fill="#fff"></path></g></g></g></svg> <span>Skype</span></label>
                                                <div className="detail-value">
                                                    <a
                                                        href={getSocialHref('skype', membersProfile?.social_skype)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {membersProfile?.social_skype}
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                        {hasDisplayValue(membersProfile?.social_facebook) && (
                                            <div className="detail-row" onClick={() => window.open(getSocialHref('facebook', membersProfile?.social_facebook), '_blank')}>
                                                <label className="detail-label"><svg id="fi_5968764" enable-background="new 0 0 512 512" height="512" viewBox="0 0 512 512" width="512" xmlns="http://www.w3.org/2000/svg"><g><path d="m512 256c0 127.78-93.62 233.69-216 252.89v-178.89h59.65l11.35-74h-71v-48.02c0-20.25 9.92-39.98 41.72-39.98h32.28v-63s-29.3-5-57.31-5c-58.47 0-96.69 35.44-96.69 99.6v56.4h-65v74h65v178.89c-122.38-19.2-216-125.11-216-252.89 0-141.38 114.62-256 256-256s256 114.62 256 256z" fill="#1877f2"></path><path d="m355.65 330 11.35-74h-71v-48.021c0-20.245 9.918-39.979 41.719-39.979h32.281v-63s-29.296-5-57.305-5c-58.476 0-96.695 35.44-96.695 99.6v56.4h-65v74h65v178.889c13.034 2.045 26.392 3.111 40 3.111s26.966-1.066 40-3.111v-178.889z" fill="#fff"></path></g></svg> <span>Facebook</span></label>
                                                <div className="detail-value">
                                                    <a
                                                        href={getSocialHref('facebook', membersProfile?.social_facebook)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {membersProfile?.social_facebook}
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                        {hasDisplayValue(membersProfile?.social_instagram) && (
                                            <div className="detail-row" onClick={() => window.open(('instagram', membersProfile?.social_instagram), '_blank')}>
                                                <label className="detail-label"><svg height="512" viewBox="0 0 152 152" width="512" xmlns="http://www.w3.org/2000/svg" id="fi_3955024"><linearGradient id="linear-gradient" gradientUnits="userSpaceOnUse" x1="22.26" x2="129.74" y1="22.26" y2="129.74"><stop offset="0" stop-color="#fae100"></stop><stop offset=".15" stop-color="#fcb720"></stop><stop offset=".3" stop-color="#ff7950"></stop><stop offset=".5" stop-color="#ff1c74"></stop><stop offset="1" stop-color="#6c1cd1"></stop></linearGradient><g id="Layer_2" data-name="Layer 2"><g id="Circle"><g id="_03.Instagram" data-name="03.Instagram"><rect id="Background" fill="url(#linear-gradient)" height="152" rx="76" width="152"></rect><g fill="#fff"><path id="Shade" d="m133.2 26c-11.08 20.34-26.75 41.32-46.33 60.9s-40.56 35.22-60.87 46.3q-1.91-1.66-3.71-3.46a76 76 0 1 1 107.45-107.48q1.8 1.8 3.46 3.74z" opacity=".1"></path><g id="Icon"><path d="m94 36h-36a22 22 0 0 0 -22 22v36a22 22 0 0 0 22 22h36a22 22 0 0 0 22-22v-36a22 22 0 0 0 -22-22zm15 54.84a18.16 18.16 0 0 1 -18.16 18.16h-29.68a18.16 18.16 0 0 1 -18.16-18.16v-29.68a18.16 18.16 0 0 1 18.16-18.16h29.68a18.16 18.16 0 0 1 18.16 18.16z"></path><path d="m90.59 61.56-.19-.19-.16-.16a20.16 20.16 0 0 0 -14.24-5.88 20.52 20.52 0 0 0 -20.38 20.67 20.75 20.75 0 0 0 6 14.61 20.19 20.19 0 0 0 14.42 6 20.73 20.73 0 0 0 14.55-35.05zm-14.59 28a13.56 13.56 0 1 1 13.37-13.56 13.46 13.46 0 0 1 -13.37 13.56z"></path><path d="m102.43 54.38a4.88 4.88 0 0 1 -4.85 4.92 4.81 4.81 0 0 1 -3.42-1.43 4.93 4.93 0 0 1 3.43-8.39 4.82 4.82 0 0 1 3.09 1.12l.1.1a3.05 3.05 0 0 1 .44.44l.11.12a4.92 4.92 0 0 1 1.1 3.12z"></path></g></g></g></g></g></svg> <span>Instagram</span></label>
                                                <div className="detail-value">
                                                    <a
                                                        href={membersProfile?.social_instagram}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {membersProfile?.social_instagram}
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                        {hasDisplayValue(membersProfile?.social_telegram) && (
                                            <div className="detail-row" onClick={() => window.open(getSocialHref('telegram', membersProfile?.social_telegram), '_blank')}>
                                                <label className="detail-label"><svg id="fi_5968804" height="512" viewBox="0 0 512 512" width="512" xmlns="http://www.w3.org/2000/svg"><linearGradient id="Oval_00000153700382965497781030000014141664724916521630_" gradientTransform="matrix(1000 0 0 -1000 1981017 6242157)" gradientUnits="userSpaceOnUse" x1="-1980.761" x2="-1980.761" y1="6242.157" y2="6241.649"><stop offset="0" stop-color="#2aabee"></stop><stop offset="1" stop-color="#229ed9"></stop></linearGradient><g id="Artboard" clipRule="evenodd" fillRule="evenodd"><circle id="Oval" cx="256" cy="256" fill="url(#Oval_00000153700382965497781030000014141664724916521630_)" r="256"></circle><path id="Path-3" d="m115.88 253.298c74.629-32.515 124.394-53.951 149.293-64.307 71.094-29.57 85.867-34.707 95.495-34.877 2.118-.037 6.853.488 9.92 2.977 4.55 3.692 4.576 11.706 4.071 17.01-3.853 40.48-20.523 138.713-29.004 184.051-3.589 19.184-10.655 25.617-17.495 26.246-14.866 1.368-26.155-9.825-40.554-19.263-22.531-14.77-35.26-23.964-57.131-38.376-25.275-16.656-8.89-25.81 5.514-40.771 3.77-3.915 69.271-63.494 70.539-68.899.159-.676.306-3.196-1.191-4.526s-3.706-.876-5.3-.514c-2.26.513-38.254 24.304-107.982 71.372-10.217 7.016-19.471 10.434-27.762 10.255-9.141-.197-26.723-5.168-39.794-9.417-16.032-5.211-28.774-7.967-27.664-16.817.578-4.611 6.926-9.325 19.045-14.144z" fill="#fff"></path></g></svg> <span>Telegram</span></label>
                                                <div className="detail-value">
                                                    <a
                                                        href={getSocialHref('telegram', membersProfile?.social_telegram)}

                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {membersProfile?.social_telegram}
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                        {hasDisplayValue(membersProfile?.social_linkedin) && (
                                            <div className="detail-row" onClick={() => window.open(('linkedin', membersProfile?.social_linkedin), '_blank')}>
                                                <label className="detail-label"><svg height="512" viewBox="0 0 152 152" width="512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g><g><circle cx="76" cy="76" fill="#0b69c7" r="76" /><g fill="#fff"><path d="m59 48.37a10.38 10.38 0 1 1 -10.37-10.37 10.38 10.38 0 0 1 10.37 10.37z" /><rect height="50.93" rx="2.57" width="16.06" x="40.6" y="63.07" /><path d="m113.75 89.47v22.17a2.36 2.36 0 0 1 -2.36 2.36h-11.72a2.36 2.36 0 0 1 -2.36-2.36v-21.48c0-3.21.93-14-8.38-14-7.22 0-8.69 7.42-9 10.75v24.78a2.36 2.36 0 0 1 -2.34 2.31h-11.34a2.35 2.35 0 0 1 -2.36-2.36v-46.2a2.36 2.36 0 0 1 2.36-2.37h11.34a2.37 2.37 0 0 1 2.41 2.37v4c2.68-4 6.66-7.12 15.13-7.12 18.73-.01 18.62 17.52 18.62 27.15z" /></g></g></g></svg> <span>LinkedIn</span></label>
                                                <div className="detail-value">
                                                    <a
                                                        href={membersProfile?.social_linkedin}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {membersProfile?.social_linkedin}
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="detail-section">
                                <h3 className="section-title">{stringsoflanguages.personalInformation}</h3>
                                <div className="detail-content">
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.fName}</div>
                                        <div className="detail-value">{FName}</div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.lName}</div>
                                        <div className="detail-value">{LName}</div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.title}</div>
                                        <div className="detail-value">{Title}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-section">
                                <h3 className="section-title">{stringsoflanguages.contactInformation}</h3>
                                <div className="detail-content">
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.email}</div>
                                        <div className="detail-value">{Email}</div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.cellPhone}</div>
                                        <div className="detail-value">{Phone}</div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="detail-label">{stringsoflanguages.licence}</div>
                                        <div className="detail-value">{LicenceNumber}</div>
                                    </div>
                                </div>
                            </div>



                            <div className="detail-section">

                                <div className="detail-row">
                                    <div className="detail-label">{stringsoflanguages.spokenLanguage}</div>
                                    <div className="detail-value">{spokenLanguage}</div>
                                </div>
                            </div>

                            <div className="detail-section flex flex-col gap-0">
                                <p className="text-sm text-gray-600 font-medium">{stringsoflanguages.shortIntro}</p>
                                <div className="detail-text">{shortIntroduction}</div>
                            </div>

                            <div className="detail-section flex flex-col gap-0">
                                <p className="text-sm text-gray-600 font-medium">{stringsoflanguages.abountMe}</p>
                                <div className="detail-text">{aboutMe}</div>
                            </div>
                        </div>
                    </div>
                    {/* <div className="lock-section">
                <svg id="fi_2889676" enableBackground="new 0 0 34 34" height="512" viewBox="0 0 34 34" width="512" xmlns="http://www.w3.org/2000/svg"><g><path d="m17 1c-5 0-9 4-9 9v4c-1.7 0-3 1.3-3 3v13c0 1.7 1.3 3 3 3h18c1.7 0 3-1.3 3-3v-13c0-1.7-1.3-3-3-3v-4c0-5-4-9-9-9zm10 16v13c0 .6-.4 1-1 1h-18c-.6 0-1-.4-1-1v-13c0-.6.4-1 1-1h1 16 1c.6 0 1 .4 1 1zm-17-3v-4c0-3.9 3.1-7 7-7s7 3.1 7 7v4z"></path><path d="m17 19c-1.7 0-3 1.3-3 3 0 1.3.8 2.4 2 2.8v2.2c0 .6.4 1 1 1s1-.4 1-1v-2.2c1.2-.4 2-1.5 2-2.8 0-1.7-1.3-3-3-3zm0 4c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1z"></path></g></svg>
            </div> */}
                </div>

                {/* Desktop only: gear icon with dropdown (6 actions from image) */}
                {!isMobile && (
                    <div className="btn-section btn-section-gear" ref={gearMenuRef}>
                        <button
                            type="button"
                            className={`btn-primary gear-btn ${gearSpinning ? 'gear-spin' : ''}`}
                            onClick={() => {
                                setGearMenuOpen((prev) => !prev);
                                setGearSpinning(true);
                                setTimeout(() => setGearSpinning(false), 500);
                            }}
                            aria-label="Options"
                            aria-expanded={gearMenuOpen}
                        >
                            <i className="fa-solid fa-gear"></i>
                        </button>
                        {gearMenuOpen && (
                            <ul className="gear-dropdown" aria-label="Options list">
                                {isBlocked ? (
                                    <li>
                                        <button type="button" className="gear-dropdown-item" onClick={() => openBlockConfirm('unblock')}>
                                            <i className="fa-solid fa-lock"></i>
                                            <span>{stringsoflanguages.unblockMember}</span>
                                        </button>
                                    </li>
                                ) : (
                                    <>
                                        <li>
                                            <button type="button" className="gear-dropdown-item" onClick={() => navigate('/')}>
                                                <i className="fa-solid fa-house"></i>
                                                <span>{stringsoflanguages.home}</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button type="button" className="gear-dropdown-item" onClick={openContractModal}>
                                                <i className="fa-solid fa-file-signature"></i>
                                                <span>{stringsoflanguages.contract}</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button type="button" className="gear-dropdown-item" onClick={openFeedOptionsModal}>
                                                <i className="fa-solid fa-wifi"></i>
                                                <span>{stringsoflanguages.feedOptions}</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button type="button" className="gear-dropdown-item" onClick={handleFeedReceivedClick}>
                                                <i className="fa-solid fa-download"></i>
                                                <span>{stringsoflanguages.feedReceived}</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button type="button" className="gear-dropdown-item" onClick={handleFeedRequestedClick}>
                                                <i className="fa-solid fa-upload"></i>
                                                <span>{stringsoflanguages.feedRequested}</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button type="button" className="gear-dropdown-item" onClick={openConnectionConfirm}>
                                                <i className={`fa-solid ${Number(checkConn) === 0 ? 'fa-circle-plus' : 'fa-circle-minus'}`}></i>
                                                <span>{Number(checkConn) === 0 ? stringsoflanguages.addConnection : stringsoflanguages.removeConnection}</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button type="button" className="gear-dropdown-item" onClick={() => openBlockConfirm('block')}>
                                                <i className="fa-solid fa-lock"></i>
                                                <span>{stringsoflanguages.blockMember}</span>
                                            </button>
                                        </li>
                                    </>
                                )}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {/* Mobile: bottom nav with 6 icons from image */}
            {isMobile && (
                <nav className="mobile-bottom-nav liquidGlass-wrapper dock members-profile-footer-nav" aria-label="Profile actions">
                    <div className="liquidGlass-effect" aria-hidden="true" />
                    <div className="liquidGlass-shine" aria-hidden="true" />
                    {isBlocked ? (
                        <div className="mobile-nav-item" aria-label="Unblock" onClick={() => openBlockConfirm('unblock')}>
                            <i className="fa-solid fa-lock"></i>
                        </div>
                    ) : (
                        <>

                            <div
                                className="mobile-nav-item"
                                role="button"
                                tabIndex={0}
                                aria-label="Home"
                                onClick={() => navigate('/')}
                            >
                                <i className="fa-solid fa-house"></i>
                            </div>
                            <div
                                className="mobile-nav-item"
                                role="button"
                                tabIndex={0}
                                aria-label="Document / Contract"
                                onClick={openContractModal}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openContractModal();
                                    }
                                }}
                            >
                                <i className="fa-solid fa-file-signature"></i>
                            </div>
                            <div className="mobile-nav-item" aria-label="Signal" onClick={openFeedOptionsModal}>
                                <i className="fa-solid fa-wifi"></i>
                            </div>
                            <div
                                className="mobile-nav-item"
                                role="button"
                                tabIndex={0}
                                aria-label="Feed Received"
                                onClick={handleFeedReceivedClick}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleFeedReceivedClick();
                                    }
                                }}
                            >
                                <i className="fa-solid fa-download"></i>
                            </div>
                            <div
                                className="mobile-nav-item"
                                role="button"
                                tabIndex={0}
                                aria-label="Feed Requested"
                                onClick={handleFeedRequestedClick}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleFeedRequestedClick();
                                    }
                                }}
                            >
                                <i className="fa-solid fa-upload"></i>
                            </div>
                            <div className="mobile-nav-item" aria-label="Remove" onClick={openConnectionConfirm}>
                                <i className={`fa-solid ${Number(checkConn) === 0 ? 'fa-circle-plus' : 'fa-circle-minus'}`}></i>
                            </div>
                            <div className="mobile-nav-item" aria-label="Lock" onClick={() => openBlockConfirm('block')}>
                                <i className="fa-solid fa-lock"></i>
                            </div>
                        </>
                    )}
                </nav>
            )}
            {connectionConfirmOpen && (
                <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Connection confirmation">
                    <div className="logout-modal">
                        <h2 className='text-white mb-2'>{connectionActionType === 'add' ? 'Add Connection?' : 'Remove Connection?'}</h2>
                        <p className='text-white mb-4'>
                            {connectionActionType === 'add'
                                ? 'Do you want to send a connection request to this member?'
                                : 'Do you want to remove this connection?'}
                        </p>
                        <div className="btn-sec">
                            <button type="button" className="save-btn" onClick={handleConfirmConnectionAction}>Yes</button>
                            <button type="button" className="cancel-btn" onClick={() => setConnectionConfirmOpen(false)}>No</button>
                        </div>
                    </div>
                </div>
            )}
            {blockConfirmOpen && (
                <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Block member confirmation">
                    <div className="logout-modal">
                        <h2 className='text-white mb-2'>{blockActionType === 'unblock' ? 'Unblock Member?' : 'Block Member?'}</h2>
                        <p className='text-white mb-4'>{blockActionType === 'unblock' ? 'Do you want to unblock this member?' : 'Do you want to block this member?'}</p>
                        <div className="btn-sec">
                            <button type="button" className="save-btn" onClick={handleConfirmBlockAction}>Yes</button>
                            <button type="button" className="cancel-btn" onClick={() => setBlockConfirmOpen(false)}>No</button>
                        </div>
                    </div>
                </div>
            )}
            {noFeedReceivedModalOpen && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="no-feed-received-title"
                    onClick={() => setNoFeedReceivedModalOpen(false)}
                >
                    <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
                        <h2 id="no-feed-received-title" className="text-white mb-6 text-center">
                            No feed sent yet
                        </h2>
                        <div className="btn-sec">
                            <button type="button" className="save-btn" onClick={() => setNoFeedReceivedModalOpen(false)}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {noFeedRequestedModalOpen && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="no-feed-requested-title"
                    onClick={() => setNoFeedRequestedModalOpen(false)}
                >
                    <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
                        <h2 id="no-feed-requested-title" className="text-white mb-6 text-center">
                            No feed requested yet
                        </h2>
                        <div className="btn-sec">
                            <button type="button" className="save-btn" onClick={() => setNoFeedRequestedModalOpen(false)}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {contractModalOpen && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="contract-modal-title"
                    onClick={() => setContractModalOpen(false)}
                >
                    <div
                        className={`logout-modal${contractDisplayValue ? ' logout-modal--contract-terms' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {contractDisplayValue ? (
                            <>
                                <h2 id="contract-modal-title" className="text-white mb-4 text-left flex-shrink-0">
                                    {stringsoflanguages.contractDetails}
                                </h2>

                                <div className="text-white text-left break-words logout-modal-contract-scroll">
                                    {renderContractWithLineBreaks(contractDisplayValue)}
                                </div>
                            </>
                        ) : (
                            <p className="text-white mb-6 text-left">{stringsoflanguages.notShared}</p>
                        )}
                        <div className={`btn-sec${contractDisplayValue ? ' contract-modal-btn-sec' : ''}`}>
                            <button type="button" className="save-btn" onClick={() => setContractModalOpen(false)}>
                                {stringsoflanguages.ok}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {feedOptionsModalOpen && (
                <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Feed options">
                    <div className="logout-modal">
                        <h2 className='text-white mb-2'>Feed Options</h2>
                        <div className="btn-sec flex-col gap-2">
                            <button
                                type="button"
                                className="save-btn"
                                onClick={() => {
                                    setFeedOptionsModalOpen(false);
                                    navigate('/feed/connection-feed-settings', {
                                        state: {
                                            member_id: memberID || '',
                                            memberID: memberID || '',
                                            user_details: membersProfile || {},
                                            member: membersProfile || {},
                                            confirmShareOnLeave: true,
                                            returnTo: {
                                                path: '/member-profile',
                                                state: {
                                                    memberID,
                                                    member: membersProfile,
                                                    from: location.state?.from || '/home/my-connection',
                                                    scrollPosition: location.state?.scrollPosition,
                                                    returnTo: location.state?.returnTo,
                                                },
                                            },
                                        },
                                    });
                                }}
                            >
                                Send my Feed
                            </button>
                            <button type="button" className="save-btn" onClick={requestFeed}>Request Feed</button>
                            <button type="button" className="cancel-btn" onClick={() => setFeedOptionsModalOpen(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            <svg style={{ display: 'none' }} aria-hidden="true">
                <filter id="glass-distortion-members-profile" x="0%" y="0%" width="100%" height="100%" filterUnits="objectBoundingBox">
                    <feTurbulence type="fractalNoise" baseFrequency="0.01 0.01" numOctaves="1" seed="5" result="turbulence" />
                    <feComponentTransfer in="turbulence" result="mapped">
                        <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
                        <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
                        <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
                    </feComponentTransfer>
                    <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
                    <feSpecularLighting in="softMap" surfaceScale="5" specularConstant="1" specularExponent="100" lightingColor="white" result="specLight">
                        <fePointLight x="-200" y="-200" z="300" />
                    </feSpecularLighting>
                    <feComposite in="specLight" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="litImage" />
                    <feDisplacementMap in="SourceGraphic" in2="softMap" scale="150" xChannelSelector="R" yChannelSelector="G" />
                </filter>
            </svg>
        </React.Fragment>
    )
}